import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Award, MapPin, Briefcase, UserCircle2, Calendar, Clock, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useLang } from '../context/LanguageContext';

export default function SpecialistPublicProfilePage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.publicGetSpecialist(slug)
      .then(setProfile)
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="max-w-[1000px] mx-auto px-6 py-16">
          <div className="h-[360px] rounded-xl bg-neutral-100 animate-pulse" />
        </div>
        <Footer />
      </>
    );
  }

  if (notFound || !profile) {
    return (
      <>
        <Header />
        <section className="py-24 bg-white text-center">
          <UserCircle2 className="w-14 h-14 mx-auto text-neutral-200" />
          <h1 className="mt-4 text-[24px] font-extrabold text-[#14110f]">{t('spec.notFound')}</h1>
          <p className="mt-2 text-[14px] text-neutral-500">
            {t('spec.notFoundDesc')}
          </p>
          <button onClick={() => nav('/specialists')}
                  className="mt-6 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-2.5 rounded-md text-[13px]">
            <ArrowLeft className="w-4 h-4" />{t('spec.backToDirectory')}
          </button>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <section className="bg-gradient-to-b from-[#fbf7ee] to-white border-b border-neutral-100 py-12">
        <div className="max-w-[1000px] mx-auto px-6">
          <Link to="/specialists" data-testid="back-to-directory"
                className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#14110f] mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />{t('spec.backToDirectory')}
          </Link>

          <div className="flex flex-col md:flex-row md:items-start gap-7">
            {profile.profile_picture_url ? (
              <img src={profile.profile_picture_url} alt={profile.name}
                   className="w-32 h-32 md:w-40 md:h-40 rounded-2xl object-cover border border-neutral-200 shadow-sm shrink-0"
                   data-testid="profile-picture" />
            ) : (
              <div data-testid="profile-picture-fallback"
                   className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-[#fff8e3] flex items-center justify-center shrink-0">
                <UserCircle2 className="w-20 h-20 text-[#c89200]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="section-eyebrow mb-1">{t('spec.titleFallback')}</p>
              <h1 data-testid="profile-name"
                  className="text-[34px] sm:text-[40px] font-extrabold text-[#14110f] leading-tight">
                {profile.name}
              </h1>
              <p className="text-[15px] text-neutral-600 mt-1.5 flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 text-[#c89200]" />
                {profile.title || t('spec.titleFallback')}
              </p>
              {profile.office_location && (
                <p className="text-[13px] text-neutral-500 mt-1 flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[#c89200]" />
                  {profile.office_location}
                </p>
              )}

              <div className="mt-5 flex flex-wrap gap-2" data-testid="profile-languages">
                {(profile.languages || []).map((l) => (
                  <span key={l}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-neutral-200 text-[11.5px] font-semibold text-neutral-700">
                    <Globe className="w-3 h-3 text-[#c89200]" />{l}
                  </span>
                ))}
              </div>

              <Link to="/book" data-testid="book-cta"
                    className="mt-6 inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-3 rounded-md text-[13.5px] shadow-sm">
                {t('spec.bookWith')} {profile.first_name || (profile.name || '').split(' ')[0]}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-[1000px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {profile.bio && (
              <div data-testid="profile-bio">
                <h2 className="text-[18px] font-extrabold text-[#14110f] mb-3">{t('spec.about')}</h2>
                <p className="text-[14px] text-neutral-700 leading-relaxed whitespace-pre-line">
                  {profile.bio}
                </p>
              </div>
            )}
            {profile.certifications && (
              <div data-testid="profile-certifications">
                <h2 className="text-[18px] font-extrabold text-[#14110f] mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#c89200]" />{t('spec.certifications')}
                </h2>
                <p className="text-[14px] text-neutral-700 leading-relaxed whitespace-pre-line">
                  {profile.certifications}
                </p>
              </div>
            )}
            {(profile.appointment_types || []).length > 0 && (
              <div data-testid="profile-services">
                <h2 className="text-[18px] font-extrabold text-[#14110f] mb-3">{t('spec.services')}</h2>
                <ul className="space-y-2">
                  {profile.appointment_types.map((t) => (
                    <li key={t.id}
                        className="rounded-md border border-neutral-200 px-4 py-3 hover:border-[#f5b300] transition">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-bold text-[#14110f] text-[14px]">{t.name}</p>
                          {t.description && (
                            <p className="text-[12.5px] text-neutral-500 mt-0.5 line-clamp-2">
                              {t.description}
                            </p>
                          )}
                        </div>
                        <p className="text-[12px] text-neutral-500 inline-flex items-center gap-1 shrink-0">
                          <Clock className="w-3 h-3" />{t.duration_minutes} min
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-xl border border-neutral-200 p-5 bg-white">
              <h3 className="text-[13.5px] font-bold text-[#14110f] mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#c89200]" />{t('spec.workingHours')}
              </h3>
              <p className="text-[12.5px] text-neutral-600">
                {t('spec.workingHoursDesc')} ({profile.timezone || 'America/Edmonton'})
              </p>
              <p className="text-[11.5px] text-neutral-400 mt-1">
                {t('spec.workingHoursHint')}
              </p>
            </div>
            <div className="rounded-xl border border-[#f5b300]/40 p-5 bg-gradient-to-br from-[#fff8e3] to-white">
              <h3 className="text-[13.5px] font-bold text-[#14110f] mb-2">{t('spec.preferTitle')}</h3>
              <p className="text-[12.5px] text-neutral-600 mb-3">
                {t('spec.preferDesc')}
              </p>
              <Link to="/account/specialist"
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#c89200] hover:underline">
                {t('spec.managePrefs')} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </>
  );
}
